// ==========================================================================
// lib/us.ts — gift lists, date nights, notes (FF-052, 10 Oct 2026)
//   Caitlin's ideas. Privacy is done by the database (029): ideas you write
//   about someone, "I've got this" claims on their wish list, and private notes
//   are never sent to the wrong person's phone in the first place.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";
import { useAuth } from "./auth";
import { privateQueryKey } from "./queryPrivacy";

function useInv(...keys: string[]) {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => { for (const k of keys) void qc.invalidateQueries({ queryKey: [k, hid] }); };
}

// ---------------------------------------------------------------------------
// Gifts
// ---------------------------------------------------------------------------
export type Gift = {
  id: string; kind: "wish" | "idea"; for_person_id: string | null; for_contact_id: string | null; title: string; link: string | null;
  price_cents: number | null; priority: 1 | 2 | 3; note: string | null; occasion: string | null; shared: boolean; created_by: string;
  given_on: string | null; created_at: string;
};
export type Claim = { id: string; gift_id: string; claimed_by: string; created_at: string };
export const PRIORITY: Record<1 | 2 | 3, string> = { 1: "Really want", 2: "Would love", 3: "Nice to have" };

export function useGifts() {
  const hid = useHouseholdId();
  const uid = useAuth().session?.user.id;
  return useQuery({
    queryKey: privateQueryKey("gifts", hid, uid), enabled: !!hid && !!uid,
    queryFn: async ({ signal }) => {
      const [g, c] = await Promise.all([
        supabase.from("gift_items").select("id,kind,for_person_id,for_contact_id,title,link,price_cents,priority,note,occasion,shared,created_by,given_on,created_at").eq("household_id", hid).is("archived_at", null).order("priority").order("created_at").abortSignal(signal),
        supabase.from("gift_claims").select("id,gift_id,claimed_by,created_at").eq("household_id", hid).is("released_at", null).abortSignal(signal),
      ]);
      if (signal.aborted) throw new Error("Session changed");
      if (g.error) throw g.error; if (c.error) throw c.error;
      return { gifts: g.data as Gift[], claims: c.data as Claim[] };
    },
  });
}

export type GiftDraft = Omit<Gift, "id" | "created_by" | "created_at" | "given_on"> & { id?: string };
export function useSaveGift() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useInv("gifts");
  return useMutation({
    mutationFn: async (d: GiftDraft) => {
      const { id, ...row } = d;
      const clean = { ...row, title: row.title.trim(), link: row.link?.trim() || null, note: row.note?.trim() || null, occasion: row.occasion?.trim() || null, shared: row.kind === "wish" ? false : row.shared };
      if (id) { const { error } = await supabase.from("gift_items").update(clean).eq("id", id); if (error) throw error; return; }
      if (!me) throw new Error("Couldn't tell who's signed in. Refresh and try again.");
      const { error } = await supabase.from("gift_items").insert({ ...clean, household_id: hid, created_by: me.id });
      if (error) throw error;
    },
    onSuccess: inv,
  });
}
export function useGiftDone() {
  const inv = useInv("gifts");
  return useMutation({
    mutationFn: async (a: { id: string; given?: boolean; archive?: boolean }) => {
      const row: Record<string, unknown> = {};
      if (a.given !== undefined) row.given_on = a.given ? new Date().toISOString().slice(0, 10) : null;
      if (a.archive) row.archived_at = new Date().toISOString();
      const { error } = await supabase.from("gift_items").update(row).eq("id", a.id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}
/** "I've got this" on someone's wish (or let it go again). */
export function useClaim() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useInv("gifts");
  return useMutation({
    mutationFn: async (a: { giftId: string; claim?: Claim }) => {
      if (a.claim) { const { error } = await supabase.from("gift_claims").update({ released_at: new Date().toISOString() }).eq("id", a.claim.id); if (error) throw error; return; }
      const { error } = await supabase.from("gift_claims").insert({ household_id: hid, gift_id: a.giftId, claimed_by: me?.id });
      if (error) throw new Error(error.code === "23505" ? "Someone's already got this one." : error.message);
    },
    onSuccess: inv,
  });
}

// ---------------------------------------------------------------------------
// Date nights
// ---------------------------------------------------------------------------
export type Setting = "home" | "out" | "away";
export type DateIdea = {
  id: string; title: string; note: string | null; link: string | null; place: string | null; setting: Setting; cost: 0 | 1 | 2 | 3;
  needs_sitter: boolean; added_by: string | null; event_id: string | null; done_on: string | null; rating: number | null; memory: string | null; created_at: string;
};
export const SETTING_LABEL: Record<Setting, string> = { home: "At home", out: "Out", away: "Away" };
export const COST_LABEL = ["Free", "$", "$$", "$$$"] as const;

export function useDateIdeas() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["dates", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("date_ideas").select("id,title,note,link,place,setting,cost,needs_sitter,added_by,event_id,done_on,rating,memory,created_at").eq("household_id", hid).is("archived_at", null).order("created_at", { ascending: false });
      if (error) throw error;
      return data as DateIdea[];
    },
  });
}
export type DateDraft = Pick<DateIdea, "title" | "note" | "link" | "place" | "setting" | "cost" | "needs_sitter"> & { id?: string };
export function useSaveDate() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useInv("dates");
  return useMutation({
    mutationFn: async (d: DateDraft) => {
      const { id, ...row } = d;
      const clean = { ...row, title: row.title.trim(), note: row.note?.trim() || null, link: row.link?.trim() || null, place: row.place?.trim() || null };
      if (id) { const { error } = await supabase.from("date_ideas").update(clean).eq("id", id); if (error) throw error; return; }
      const { error } = await supabase.from("date_ideas").insert({ ...clean, household_id: hid, added_by: me?.id ?? null });
      if (error) throw error;
    },
    onSuccess: inv,
  });
}
export function useUpdateDate() {
  const inv = useInv("dates", "events");
  return useMutation({
    mutationFn: async (a: { id: string; patch: Partial<Pick<DateIdea, "event_id" | "done_on" | "rating" | "memory">> & { archive?: boolean } }) => {
      const { archive, ...rest } = a.patch;
      const row: Record<string, unknown> = { ...rest };
      if (archive) row.archived_at = new Date().toISOString();
      const { error } = await supabase.from("date_ideas").update(row).eq("id", a.id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------
export type Note = { id: string; title: string | null; body: string; private: boolean; pinned: boolean; colour: string | null; created_by: string; updated_at: string };
export function useNotes() {
  const hid = useHouseholdId();
  const uid = useAuth().session?.user.id;
  return useQuery({
    queryKey: privateQueryKey("notes", hid, uid), enabled: !!hid && !!uid,
    queryFn: async ({ signal }) => {
      const { data, error } = await supabase.from("notes").select("id,title,body,private,pinned,colour,created_by,updated_at").eq("household_id", hid).is("archived_at", null).order("pinned", { ascending: false }).order("updated_at", { ascending: false }).abortSignal(signal);
      if (error) throw error;
      if (signal.aborted) throw new Error("Session changed");
      return data as Note[];
    },
  });
}
export type NoteDraft = Pick<Note, "title" | "body" | "private" | "pinned" | "colour"> & { id?: string };
export function useSaveNote() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useInv("notes");
  return useMutation({
    mutationFn: async (d: NoteDraft & { archive?: boolean }) => {
      const { id, archive, ...row } = d;
      const clean = { ...row, title: row.title?.trim() || null };
      if (id) {
        const { error } = await supabase.from("notes").update(archive ? { archived_at: new Date().toISOString() } : clean).eq("id", id);
        if (error) throw error; return;
      }
      if (!me) throw new Error("Couldn't tell who's signed in. Refresh and try again.");
      const { error } = await supabase.from("notes").insert({ ...clean, household_id: hid, created_by: me.id });
      if (error) throw error;
    },
    onSuccess: inv,
  });
}
